import type { Logger } from "@expert/logging";
import type { ErrorBoundaryProps, FallbackProps } from "react-error-boundary";

import { sendErrorMetric } from "@expert/cloudwatch-metrics";

import { RenderError } from "./RenderError";

export function createFallbackRender(logger: Logger) {
    return function fallbackRender({ error }: FallbackProps) {
        if (error instanceof Error) {
            logger.fatal({ error, errorMessage: error.message }, "Error Boundary caught an error");
        } else {
            logger.fatal({ error }, "Error Boundary caught an error");
        }
        return <RenderError error={error} />;
    };
}

/**
 * Error-boundary props that log the crash and emit one `RenderCrash{component}` metric. `component`
 * scopes the fleet-wide alert to the owning team so another team's boundary can't pollute it (#2956).
 * Emitted from `onError` (fires once per catch), not the fallback render (which re-runs on re-render).
 *
 * IMPORTANT: `component` must be registered in `SUPPORTED_COMPONENTS` in expert-workspace-api
 * (packages/shared-types/metrics.ts). The backend /metrics collector drops any unlisted component
 * value fail-open (a warn, no error), so an unregistered value never reaches Mimir — the metric
 * carries no `component` label and any alert filtering on it sits in NoData. Add your value there in
 * the same change. (This is how the first RenderCrash alert shipped dead — expert-workspace-api#4623.)
 */
export function trackedBoundaryProps(logger: Logger, component: string) {
    return {
        fallbackRender: createFallbackRender(logger),
        onError: () => {
            void sendErrorMetric("RenderCrash", { component });
        },
    } satisfies Pick<ErrorBoundaryProps, "fallbackRender" | "onError">;
}
